import {Store} from "@reduxjs/toolkit"
import logger from "@next/core-logger"
import {SecondaryNav, DefaultConfig} from "../../models/secondary-nav"
import {getSecondaryNavData} from "../../api/secondary-nav"
import {DEFAULT_SECONDARY_NAV_VERSION} from "../../config/constants"
import {SettingsSdkKeys} from "../../models/settings"
import {FeatureSettings} from "../../models/feature-settings"
import {getQueryParameters} from "../primary-nav"
import {
    GET_SECONDARY_NAV_DATA,
    SET_DEFAULT_SECONDARY_CONFIG,
    SET_IS_IMAGE_PLACEHOLDER_ENABLED,
    SET_IS_IN_SECONDARY_NAV,
    SET_SECONDARY_NAV_DATA,
    SET_IS_IN_TERTIARY_NAV,
    SET_TERTIARY_NAV_INDEX,
} from "./consts"
import type {
    GetSecondaryNavData,
    SecondaryNavDuckState,
    SetDefaultSecondaryConfig,
    SetIsImagePlaceholderEnabled,
    SetIsInSecondaryNav,
    SetSecondaryNavData,
    SetIsInTertiaryNav,
    SetTertiaryNavIndex,
} from "./types"

export const initialState: SecondaryNavDuckState = {
    departmentIds: [],
    catalogues: {},
    isPending: false,
    isInSecondaryNav: false,
    isImagePlaceholderEnabled: false,
    config: {
        version: DEFAULT_SECONDARY_NAV_VERSION,
        country: "",
    },
    isInTertiaryNav: false,
    tertiaryNavIndex: -1,
}

const getImagePlaceholderEnabled = (config: FeatureSettings): boolean =>
    !!config[SettingsSdkKeys.imagePlaceHolder]?.EnableImagePlaceholder

const setImagePlaceholderEnabled = (store: Store, isImagePlaceholderEnabled: boolean) =>
    store.dispatch({
        type: SET_IS_IMAGE_PLACEHOLDER_ENABLED,
        payload: isImagePlaceholderEnabled,
    })

export const updateImagePlaceholderSettings = (store: Store, config: FeatureSettings) =>
    setImagePlaceholderEnabled(store, getImagePlaceholderEnabled(config))

export const setDefaultConfig = (store: Store, config: DefaultConfig) =>
    store.dispatch({
        type: SET_DEFAULT_SECONDARY_CONFIG,
        payload: config,
    })

export const setIsInSecondaryMeganav = (isInSecondaryNav: boolean) => dispatch =>
    dispatch({
        type: SET_IS_IN_SECONDARY_NAV,
        payload: isInSecondaryNav,
    })

export const setIsInTertiaryNav = (isInTertiaryNav: boolean) => dispatch =>
    dispatch({
        type: SET_IS_IN_TERTIARY_NAV,
        payload: isInTertiaryNav,
    })

export const setTertiaryNavIndex = (tertiaryNavIndex: number) => dispatch =>
    dispatch({
        type: SET_TERTIARY_NAV_INDEX,
        payload: tertiaryNavIndex,
    })

const makeRequest = async (siteUrl: string, headers, dispatch, department: string, queryParameters: string) => {
    try {
        const response = await getSecondaryNavData(siteUrl, headers, department, queryParameters)
        dispatch({type: SET_SECONDARY_NAV_DATA, payload: response})
    } catch (error) {
        logger.error(error)
        dispatch({type: SET_SECONDARY_NAV_DATA, payload: new SecondaryNav()})
    }
}

export const getSecondaryNavDataThunk = (department: string) => async (dispatch, getState) => {
    const {
        request: {headers, siteUrl},
        secondarynav: {catalogues},
    } = getState()
    const queryParameters = getQueryParameters(getState)
    const dept = department.toLowerCase()
    const departmentCatalogueExists = catalogues[dept]
    if (departmentCatalogueExists) return

    dispatch({type: GET_SECONDARY_NAV_DATA, payload: true})
    await makeRequest(siteUrl, headers, dispatch, dept, queryParameters)
}

const mergeData = (oldData: SecondaryNavDuckState, newData: SecondaryNav): SecondaryNavDuckState => {
    const lowercaseTitle: string = newData.title.toLowerCase()
    const catalogues = {...oldData.catalogues, [lowercaseTitle]: newData}
    const newDepartmentIds: string[] = oldData.catalogues[lowercaseTitle] ? [] : [lowercaseTitle]
    const departmentIds: string[] = oldData.departmentIds.concat(newDepartmentIds)

    return {
        ...oldData,
        catalogues,
        departmentIds,
        isPending: false,
    }
}

const reducer = (
    state: SecondaryNavDuckState = initialState,
    action:
        | SetSecondaryNavData
        | GetSecondaryNavData
        | SetIsInSecondaryNav
        | SetDefaultSecondaryConfig
        | SetIsImagePlaceholderEnabled
        | SetIsInTertiaryNav
        | SetTertiaryNavIndex,
): SecondaryNavDuckState => {
    switch (action.type) {
        case GET_SECONDARY_NAV_DATA: {
            return {...state, isPending: action.payload}
        }
        case SET_SECONDARY_NAV_DATA: {
            return mergeData(state, action.payload)
        }
        case SET_IS_IN_SECONDARY_NAV:
            return {
                ...state,
                isInSecondaryNav: action.payload,
            }
        case SET_IS_IMAGE_PLACEHOLDER_ENABLED:
            return {
                ...state,
                isImagePlaceholderEnabled: action.payload,
            }
        case SET_DEFAULT_SECONDARY_CONFIG:
            return {
                ...state,
                config: action.payload,
            }
        case SET_IS_IN_TERTIARY_NAV:
            return {
                ...state,
                isInTertiaryNav: action.payload,
            }
        case SET_TERTIARY_NAV_INDEX:
            return {
                ...state,
                tertiaryNavIndex: action.payload,
            }
        default:
            return state
    }
}

export default reducer
